import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "./ui/carousel";

const SLIDES = [
  {
    id: "marca",
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2000&q=80",
    alt: "Interior moderno con cuadros decorativos",
    eyebrow: "El arte de ser extraordinario",
    headline: (
      <>
        Arte que transforma
        <br />
        <span className="italic text-[#F4C8B8]">tus espacios</span>.
      </>
    ),
    subhead:
      "Cuadros personalizados en MDF que capturan momentos únicos. Hechos en Norte de Santander, enviados con garantía a todo el país.",
    cta: { label: "Explorar categorías", target: "/categorias" },
    secondary: { label: "Pide un cuadro personalizado", target: "#personalizados" },
  },
  {
    id: "personalizados",
    image:
      "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=2000&q=80",
    alt: "Cuadro enmarcado en una pared de hogar",
    eyebrow: "Cuadros personalizados",
    headline: (
      <>
        Tu foto favorita,
        <br />
        <span className="italic text-[#F4C8B8]">convertida en arte</span>.
      </>
    ),
    subhead:
      "Sube la foto de tu mascota, tu familia o ese momento especial y la imprimimos enmarcada en MDF de alta calidad.",
    cta: { label: "Pedir el mío", target: "#personalizados" },
    secondary: { label: "Ver categorías", target: "/categorias" },
  },
  {
    id: "categorias",
    image:
      "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=2000&q=80",
    alt: "Pared de galería con varios cuadros enmarcados",
    eyebrow: "12 categorías para explorar",
    headline: (
      <>
        Un estilo para
        <br />
        <span className="italic text-[#F4C8B8]">cada pared</span>.
      </>
    ),
    subhead:
      "Mascotas, abstracto, paisajes, frases, infantil y más. Encuentra la categoría que va con tu espacio.",
    cta: { label: "Ver categorías", target: "/categorias" },
    secondary: { label: "Conócenos", target: "#nosotros" },
  },
];

const AUTOPLAY_MS = 6000;

export const HeroSection = () => {
  const navigate = useNavigate();
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);

  // CTAs: las rutas ("/...") navegan; los anchors ("#...") hacen smooth scroll.
  const goToTarget = (e, target) => {
    e.preventDefault();
    if (target.startsWith("/")) {
      navigate(target);
      return;
    }
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  useEffect(() => {
    if (!api || paused) return;
    const timer = setInterval(() => api.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [api, paused, selected]);

  const goTo = useCallback((i) => api?.scrollTo(i), [api]);

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative isolate w-full h-[88vh] min-h-[600px] max-h-[860px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Carousel
        setApi={setApi}
        opts={{ loop: true }}
        className="h-full"
        data-testid="hero-carousel"
      >
        <CarouselContent className="h-[88vh] min-h-[600px] max-h-[860px] ml-0">
          {SLIDES.map((slide, i) => (
            <CarouselItem
              key={slide.id}
              data-testid={`hero-slide-${i + 1}`}
              className="relative h-full pl-0"
            >
              {/* Background image */}
              <img
                src={slide.image}
                alt={slide.alt}
                className="absolute inset-0 h-full w-full object-cover"
              />
              {/* Overlay gradient for legibility */}
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/55 via-slate-900/35 to-slate-900/70" />
              {/* Subtle grain */}
              <div
                className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
                }}
              />

              <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-24 md:pb-28">
                <div className="max-w-2xl">
                  <span
                    data-testid="hero-eyebrow"
                    className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.22em] text-[#C8D5C0] mb-5 md:mb-7"
                  >
                    <span className="block h-px w-8 bg-[#C8D5C0]" />
                    {slide.eyebrow}
                  </span>

                  <h1
                    data-testid="hero-headline"
                    className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white"
                  >
                    {slide.headline}
                  </h1>

                  <p
                    data-testid="hero-subhead"
                    className="mt-5 md:mt-7 text-base md:text-lg text-slate-200/90 max-w-xl leading-relaxed"
                  >
                    {slide.subhead}
                  </p>

                  <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4">
                    <a
                      href={slide.cta.target}
                      onClick={(e) => goToTarget(e, slide.cta.target)}
                      data-testid="hero-cta-button"
                      className="group inline-flex items-center gap-2 bg-[#E85A3C] hover:bg-[#D3492D] text-white px-7 py-3.5 rounded-full font-medium text-sm md:text-base shadow-lg shadow-[#E85A3C]/30 transition-all duration-300 hover:-translate-y-0.5"
                    >
                      {slide.cta.label}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                    <a
                      href={slide.secondary.target}
                      onClick={(e) => goToTarget(e, slide.secondary.target)}
                      data-testid="hero-secondary-cta"
                      className="inline-flex items-center gap-2 px-2 py-3.5 text-sm md:text-base font-medium text-white/90 hover:text-white transition-colors duration-300 border-b border-transparent hover:border-white/60"
                    >
                      {slide.secondary.label}
                    </a>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Dot indicators */}
      <div
        data-testid="hero-dots"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-10"
      >
        {SLIDES.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(i)}
            data-testid={`hero-dot-${i + 1}`}
            aria-label={`Ir al slide ${i + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              selected === i
                ? "w-8 bg-[#E85A3C]"
                : "w-2.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
